package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initDocsBootstrapPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/DocsBootstrap.md", FrontMatterElement.Builder { addScalar("title", "docs-bootstrap: a documentation format with a gate"); addScalar("searchTitle", "Layered project documentation for coding agents, with machine checks"); addScalar("date", "2026-08-19"); addScalar("description", "Documentation formats for coding agents are written as prose rules that nothing verifies. docs-bootstrap is the same idea with checks that can fail, run by CI against the repository's own example."); }))
}

@Page("/blog/docs-bootstrap")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun DocsBootstrapPage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Documentation meant for a coding agent to read is usually described in prose: keep the definitions tight, point at the code instead of retelling it, say which parts are guesses. The rules read like a format and behave like advice, because nothing looks at a document afterwards and says whether it obeyed them.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/docs-bootstrap") {
            org.jetbrains.compose.web.dom.Text("docs-bootstrap")
        }
        org.jetbrains.compose.web.dom.Text(" is a documentation format for codebases, plus the checks that keep it honest: layers linked by ids, every document carrying paths into the code, and six scripts CI runs on every push. MIT, Python 3.11 with PyYAML, and it also ships as a Claude Code skill.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The failure mode is not missing documentation. It is documentation that stops matching the code while nothing says so. A path rots because somebody renamed a directory in another repository. A page describing intent gets merged and starts reading as fact. The index in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docs/README.md")
        }
        org.jetbrains.compose.web.dom.Text(" loses a file, and the only way to find out is to notice.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("\"Keep the definitions tight\" has no failing case, so it is advice with a format's typography. The question this started from is which of those rules can be made to fail, and what is honestly left over when they cannot.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-does") }) {
        org.jetbrains.compose.web.dom.Text("What it does")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Four layers — ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("features/")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("screens/")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("api/")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("services/")
            }
            org.jetbrains.compose.web.dom.Text(" — with an optional ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("research/")
            }
            org.jetbrains.compose.web.dom.Text(" above them. A missing directory is a valid answer; a renamed one is not.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Cross-layer links are ids in the frontmatter and markdown links in the body, so the graph resolves without opening every file.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Every document must carry at least one path into the code — the one structural rule the checker enforces, and the reason the format exists.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("BDD scenarios are the acceptance criteria; one covered by a test says so on an ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("**Automated:**")
            }
            org.jetbrains.compose.web.dom.Text(" line, and the absence of that line is the visible part.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Backlog in two shapes: ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("BACKLOG.md")
            }
            org.jetbrains.compose.web.dom.Text(" with ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("M-NN")
            }
            org.jetbrains.compose.web.dom.Text(" items under milestones, or a file per item plus a generated index for larger trees.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Three blocking checks, two reports, one for the packaging, and two that only make sense on a branch: ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("status: draft")
            }
            org.jetbrains.compose.web.dom.Text(" is an error on the default branch, and an item number taken by a branch that merged first is an error on a pull request.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("installation") }) {
        org.jetbrains.compose.web.dom.Text("Installation")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("As a Claude Code plugin, from the repository's own marketplace:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""claude plugin marketplace add youndie/docs-bootstrap
claude plugin install docs-bootstrap@docs-bootstrap
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Or as a clone. The directory name has to match the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("name:")
        }
        org.jetbrains.compose.web.dom.Text(" in the skill's frontmatter, so the clone path is the installation:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""git clone https://github.com/youndie/docs-bootstrap ~/.claude/skills/docs-bootstrap
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The checks need neither: copy ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("scripts/")
        }
        org.jetbrains.compose.web.dom.Text(" into a project and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("make check")
        }
        org.jetbrains.compose.web.dom.Text(" works against a hand-written tree.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-a-document-looks-like") }) {
        org.jetbrains.compose.web.dom.Text("What a document looks like")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-markdown") }) { org.jetbrains.compose.web.dom.Text("""---
id: feature-borrow-and-return
type: feature
status: active
involved_services: [catalog-api, loans-service]
client_entries: [screen-checkout-desk]
---

## Code anchors

| Service | Code |
|---|---|
| loans-service | `src/loans_service/domain/loan.py` — the state machine |
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("id")
        }
        org.jetbrains.compose.web.dom.Text(" equals the filename, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("client_entries: []")
        }
        org.jetbrains.compose.web.dom.Text(" is an answer while a missing field is a question, and anchors may be written from the repository root, from the module root, or abbreviated with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("...")
        }
        org.jetbrains.compose.web.dom.Text(": matching is by suffix. The gate reports what it found rather than a score:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""Documents: 11
No errors
The coverage map matches the files
Anchors: 91 - found 90, not found 0, skipped 1
""") } }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-is-checked-and-what-is-only-reported") }) {
        org.jetbrains.compose.web.dom.Text("What is checked, and what is only reported")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Errors are things one cannot be wrong about: a broken link, a missing required field, an ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("id")
        }
        org.jetbrains.compose.web.dom.Text(" that is not the filename. Warnings are rules with legitimate exceptions, such as an orphan document. Reports carry no judgement.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two rules deliberately did not become gates. The share of automated scenarios is a number rather than a threshold, because a gate on that figure teaches people to write the line without the test. Whether anchors still resolve is a report because an anchor breaks from a refactor in somebody else's repository, and a path quoted ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("as obsolete")
        }
        org.jetbrains.compose.web.dom.Text(" is indistinguishable from a live one by machine. A gate that cannot be passed honestly gets switched off within a month, taking the useful checks with it.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-coverage-map-is-checked-not-generated") }) {
        org.jetbrains.compose.web.dom.Text("The coverage map is checked, not generated")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docs/README.md")
        }
        org.jetbrains.compose.web.dom.Text(" ends with every document, grouped by layer, one line of description each. Generating that list would destroy the only thing it is read for: neither the grouping nor the descriptions can be derived from the files. The machine owns the membership and the counters, the human owns the meaning, and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("--fix")
        }
        org.jetbrains.compose.web.dom.Text(" appends missing entries marked as placeholders instead of inventing sentences. The backlog index is the opposite case and is generated: a task's status lives in the task's own file, and storing it twice is how an index starts lying.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("in-use") }) {
        org.jetbrains.compose.web.dom.Text("In use")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two public projects are documented this way, both single-repository and without a client, so both skip ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("screens/")
        }
        org.jetbrains.compose.web.dom.Text(" and keep the backlog as a root ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("BACKLOG.md")
        }
        org.jetbrains.compose.web.dom.Text(":")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/booblik") {
                org.jetbrains.compose.web.dom.Text("booblik")
            }
            org.jetbrains.compose.web.dom.Text(", a JVM message broker: 11 documents — architecture and use-case research, two features, a wire-protocol reference, five module documents.")
        }
        org.jetbrains.compose.web.dom.Li {
            com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/mongkn") {
                org.jetbrains.compose.web.dom.Text("mongkn")
            }
            org.jetbrains.compose.web.dom.Text(", a Kotlin/Native binding over ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("mongo-c-driver")
            }
            org.jetbrains.compose.web.dom.Text(": 8 documents, plus coverage and performance pages that sit outside the layers.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limitations") }) {
        org.jetbrains.compose.web.dom.Text("Limitations")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Neither of the two trees above passes the gate today")
            }
            org.jetbrains.compose.web.dom.Text(" — booblik reports 6 errors, mongkn 9. That is drift rather than a limit of the format: both were written under an earlier version of the skill and never brought forward, so their research documents predate ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("type")
            }
            org.jetbrains.compose.web.dom.Text(" becoming mandatory, two of their statuses are outside the vocabulary, and one document has no frontmatter at all. A tree written to the current spec passes; that was checked by documenting a small project from scratch in the same single-repository layout.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("code_anchors.py")
            }
            org.jetbrains.compose.web.dom.Text(" harvests every backticked path in a document, not only the anchors table. A page mentioning ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("/usr/local")
            }
            org.jetbrains.compose.web.dom.Text(" in prose gets it counted and reported as missing.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("That check also needs the repositories checked out side by side. Without ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("--repos")
            }
            org.jetbrains.compose.web.dom.Text(" it prints \"not checked\", which is not the same as \"nothing wrong\".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Nothing here verifies that a sentence is ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("true")
            }
            org.jetbrains.compose.web.dom.Text(". The checks confirm that the things a document names exist and that the graph resolves; whether the paragraph next to the path describes the code is a question for a reader.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("spec_version")
            }
            org.jetbrains.compose.web.dom.Text(" is 1, and the worked example is synthetic: a three-service lending system written for the repository, with real code underneath so every anchor resolves. It says nothing about how the format behaves on a large tree.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/docs-bootstrap") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/docs-bootstrap")
        }
    }
}
